import {memo, useMemo} from 'react'
import {compact} from 'lodash-es'
import ProductTypeDescription from '~/components/product-details/ProductTypeDescription'
import ProductTypeImage from '~/components/product-details/ProductTypeImage'
import MaterialImage from '~/components/product-details/MaterialImage'
import ResponsiveDialog from '~/components/common/ResponsiveDialog'
import {ProductWithIds} from '~/client/spreadshop/missing-types'
import {filterProductVariants} from '~/utils/product'
import {ProductAppearance} from '~/gql/graphql'
import {useTranslations} from 'use-intl'

type ProductDialogProps = {
    onClose: () => void
    product: ProductWithIds
    selectedAppearance?: ProductAppearance
    open: boolean
}

const ProductTypeDialog = memo(
    ({onClose, product, selectedAppearance, open}: ProductDialogProps) => {
        const t = useTranslations('productDetails.productTypeDialog')
        const productType = product.type

        const appearanceName = useMemo<string | undefined>(() => {
            if (selectedAppearance?.name) {
                return selectedAppearance.name
            }

            const appearanceId = product.defaultAppearanceId
            if (!appearanceId) {
                return undefined
            }

            const firstVariant = filterProductVariants(compact(product.variants), {appearanceId})[0]

            return firstVariant.appearance?.name ?? undefined
        }, [selectedAppearance?.name, product.variants, product.defaultAppearanceId])

        if (!productType) {
            return null
        }

        return (
            <ResponsiveDialog
                open={open}
                onOpenChange={onClose}
                title={t('title')}
                description={productType.name}
                ssr
            >
                <div className="pb-8 px-4 overflow-y-auto">
                    <h3 className="mb-1 font-bold">{productType.name}</h3>
                    <div className="my-2 empty:hidden grid grid-cols-2 gap-4 justify-center items-center max-w-screen-sm mx-auto">
                        <ProductTypeImage
                            className="shadow pointer-events-none select-none"
                            product={product}
                            appearanceId={selectedAppearance?.id ?? undefined}
                        />
                        <MaterialImage
                            className="shadow pointer-events-none select-none"
                            product={product}
                            appearanceId={selectedAppearance?.id ?? undefined}
                        />
                    </div>
                    <div className="mb-2">
                        <p>{t('brand', {brand: productType.brand})}</p>
                        {!!appearanceName && <p>{t('color', {color: appearanceName})}</p>}
                    </div>
                    <ProductTypeDescription>
                        {String(productType.description)}
                    </ProductTypeDescription>
                </div>
            </ResponsiveDialog>
        )
    }
)

export default ProductTypeDialog
